import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { IRequestState, UploadedDrawingFileType } from '@modules/request';
import {
  CapaQuotationDetailedServiceType,
  CapaQuotationServiceType
} from '@generated/graphql';
import useRequest from '@hooks/useRequest';
import useUserActionLog, {
  Enum_Action_Page_Enum,
  CapaActivityType
} from '@hooks/useUserActionLog';
import useClient from '@hooks/useClient';
import useQueryString from '@hooks/useQueryString';
import RfqRequestPreview from '@routes/RfqRequestPreview';
import BottomNav from '@routes/Common/BottomNav';
import CommonLayout from '@routes/Common/CommonLayout';
import { RequestQueryString } from '@routes/NewRequest/types';
import RequestForQuotationPreviewDialog from './Dialogs/RequestForQuotationPreviewDialog';
import MdGuideDialog from './Dialogs/MdGuideDialog';
import RequestForQuotationSheetMetalService from './RequestForQuotationSheetMetalService';
import RequestForQuotationHeader from './RequestForQuotationHeader';
import RequestForQuotationPresenter from './RequestForQuotationPresenter';
import RequestForQuotationRef from './RequestForQuotationRef';
import RequestForQuotationSecurity from './RequestForQuotationSecurity';
import RequestForQuotationUpload from './RequestForQuotationUpload';
import RequestForQuotationProjectInfo from './RequestForQuotationProjectInfo';
import RequestForQuotationSubmit from './RequestForQuotationSubmit';
import { DialogTypes, initialDialogState } from './types';
import RegisterConnectDialog from './Dialogs/RegisterConnectDialog';
import ConnectDriveDialog from './Dialogs/ConnectDriveDialog';
import RequestForQuotationConsultMethod from './RequestForQuotationConsultMethod';
import HowToModal from './Dialogs/HowtoModal';

interface Props {
  hanledPreviewDialogOpen: () => void;
}

const RequestForQuotationContainer = ({ hanledPreviewDialogOpen }: Props) => {
  const history = useHistory();
  const { onInsertUserActionLogOne } = useUserActionLog();
  const { clientState, hasRequiredInfo } = useClient();
  const isAuth = Boolean(clientState?.accessToken);

  const [dialogState, setDialogState] = useState<DialogTypes>(
    initialDialogState
  );
  const { query } = useQueryString<RequestQueryString>();
  const matches = useMediaQuery(useTheme().breakpoints.down('xs'));
  const {
    requestState,
    serviceType,
    onInitRfqState,
    onUpdateRfqFile,
    onUpdateRfqInfo,
    onBatchUpdateRfqFile,
    onDeleteRfqFile,
    hasNotEndedRequest,
    initialState
  } = useRequest();

  const methods = useForm<IRequestState>({
    defaultValues: {
      ...requestState,
      referenceFiles: requestState.uploadedReferenceFiles?.map((file) => ({
        fileId: file.fileId,
        fileName: file.fileName
      }))
    }
  });

  const currentServiceType = query.serviceType;
  const currentDetailedServiceType = query.detailedServiceType;
  const uploadedFilesError = Object.keys(methods.errors);
  const uploadedDrawingFile = requestState.uploadedDrawingFiles ?? [];

  const reWrite = query.reWrite === 'true';

  const logType = useMemo(() => {
    if (reWrite) {
      if (serviceType === CapaQuotationServiceType.Cnc) {
        return Enum_Action_Page_Enum.Page_RFQ_Rewrite_CNC_InputForm;
      }

      if (serviceType === CapaQuotationServiceType.Printing) {
        return Enum_Action_Page_Enum.Page_RFQ_Rewrite_PRINTING_InputForm;
      }

      if (serviceType === CapaQuotationServiceType.Inj) {
        return Enum_Action_Page_Enum.Page_RFQ_Rewrite_INJ_InputForm;
      }

      if (serviceType === CapaQuotationServiceType.SheetMetal) {
        return Enum_Action_Page_Enum.Page_RFQ_Rewrite_SHEET_METAL_InputForm;
      }

      if (serviceType === CapaQuotationServiceType.Casting) {
        return Enum_Action_Page_Enum.Page_RFQ_Rewrite_CASTING_InputForm;
      }
    }

    if (serviceType === CapaQuotationServiceType.Cnc) {
      return Enum_Action_Page_Enum.Page_RFQ_CNC_InputForm;
    }

    if (serviceType === CapaQuotationServiceType.Printing) {
      return Enum_Action_Page_Enum.Page_RFQ_PRINTING_InputForm;
    }

    if (serviceType === CapaQuotationServiceType.Inj) {
      return Enum_Action_Page_Enum.Page_RFQ_INJ_InputForm;
    }

    if (serviceType === CapaQuotationServiceType.SheetMetal) {
      return Enum_Action_Page_Enum.Page_RFQ_SHEET_METAL_InputForm;
    }

    if (serviceType === CapaQuotationServiceType.Casting) {
      return Enum_Action_Page_Enum.Page_RFQ_CASTING_InputForm;
    }
  }, [serviceType, reWrite]);

  const handleUpdateRfqFile = (payload: UploadedDrawingFileType) => {
    onUpdateRfqFile({ ...payload });
  };

  const handleUpdateRfqInfo = (payload: Partial<IRequestState>) => {
    onUpdateRfqInfo({ ...requestState, ...payload });
  };

  const handleBatchUpdateRfqFile = (payload: UploadedDrawingFileType) => {
    onBatchUpdateRfqFile({ ...payload });
  };

  const handleDeleteRfqFile = (payload: UploadedDrawingFileType) => {
    if (payload.fileId) {
      onDeleteRfqFile(payload.fileId);
    }
  };

  const onCheck = methods.handleSubmit(
    (data) => {
      const invalidDrawingFiles = uploadedDrawingFile.filter(
        (file) => !file.isValid
      );
      if (invalidDrawingFiles && invalidDrawingFiles.length > 0) {
        for (const file of invalidDrawingFiles) {
          methods.setError(file.fileId as string, { type: 'required' });
        }
      }
      if (!hasRequiredInfo) {
        return setDialogState({ ...initialDialogState, profileUpdate: true });
      }

      if (uploadedFilesError.length === 0 && invalidDrawingFiles.length === 0) {
        setDialogState({ ...initialDialogState, preview: true });
        hanledPreviewDialogOpen();
      }
    },
    () => {
      const invalidDrawingFiles = uploadedDrawingFile.filter(
        (file) => !file.isValid
      );

      if (invalidDrawingFiles && invalidDrawingFiles.length > 0) {
        for (const file of invalidDrawingFiles) {
          methods.setError(file.fileId as string, { type: 'required' });
        }
      }
    }
  );

  useEffect(() => {
    if (!currentServiceType) {
      onInitRfqState();
      history.push('/request');
    }
  }, [history, requestState, onInitRfqState, currentServiceType]);

  useEffect(() => {
    if (!hasNotEndedRequest && currentServiceType && currentDetailedServiceType)
      onUpdateRfqInfo({
        ...initialState,
        serviceType: currentServiceType,
        detailedServiceType: currentDetailedServiceType
      });

    //eslint-disable-next-line
  }, []);

  /*
    로그
  */
  useEffect(() => {
    if (logType) {
      onInsertUserActionLogOne(logType, CapaActivityType.PageVisit);
    }
  }, [onInsertUserActionLogOne, logType]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!isAuth) return <RfqRequestPreview />;

  return (
    <CommonLayout
      fullHeight
      gnbTabs={isAuth ? null : null}
      bottomNav={isAuth ? <BottomNav /> : null}
    >
      {dialogState.preview && (
        <RequestForQuotationPreviewDialog
          open={true}
          onClose={() => {
            setDialogState(initialDialogState);
            hanledPreviewDialogOpen();
          }}
        />
      )}

      {dialogState.registerConnect && (
        <RegisterConnectDialog
          open={true}
          onClose={() =>
            setDialogState({ ...dialogState, registerConnect: false })
          }
          onOpenDialog={(type: keyof DialogTypes) =>
            setDialogState({ ...dialogState, [type]: true })
          }
        />
      )}

      {dialogState.connectDrive && (
        <ConnectDriveDialog
          open={true}
          matches={matches}
          serviceType={serviceType}
          onClose={() => setDialogState({ ...initialDialogState })}
        />
      )}

      {dialogState.MdGuide && (
        <MdGuideDialog
          open={true}
          onClose={() => setDialogState({ ...initialDialogState })}
        />
      )}

      {dialogState.howTo && (
        <HowToModal
          open={true}
          onClose={() => setDialogState({ ...initialDialogState })}
          type={serviceType}
        />
      )}

      {/* {dialogState.profileUpdate && (
        <ProfileUpdateDialog
          isSubmit={true}
          openControl={true}
          closeControl={() => setDialogState({ ...initialDialogState })}
        />
      )} */}

      <FormProvider {...methods}>
        <RequestForQuotationPresenter
          // 서비스타입
          headerComponent={
            <RequestForQuotationHeader
              matches={matches}
              serviceType={serviceType}
              detailedServiceType={
                requestState.detailedServiceType as CapaQuotationDetailedServiceType
              }
              uploadedDrawingFiles={requestState.uploadedDrawingFiles}
              onOpenMdGuidDialog={() =>
                setDialogState({ ...initialDialogState, MdGuide: true })
              }
              onOpenDialog={(type: keyof DialogTypes) =>
                setDialogState({ ...dialogState, [type]: true })
              }
            />
          }
          // 도면파일 업로드
          uploadComponent={
            <RequestForQuotationUpload
              matches={matches}
              requestState={requestState}
              uploadedFilesError={uploadedFilesError}
              handleUpdateRfqFile={handleUpdateRfqFile}
              handleBatchUpdateRfqFile={handleBatchUpdateRfqFile}
              handleDeleteRfqFile={handleDeleteRfqFile}
              onOpenDialog={(type: keyof DialogTypes) =>
                setDialogState({ ...dialogState, [type]: true })
              }
            />
          }
          // 도면 보안
          securityComponent={
            <RequestForQuotationSecurity
              handleUpdateRfqInfo={handleUpdateRfqInfo}
            />
          }
          // 판금 추가
          sheetMetalComponent={
            serviceType === CapaQuotationServiceType.SheetMetal ? (
              <RequestForQuotationSheetMetalService />
            ) : undefined
          }
          // 상세요청, 참고파일
          refComponent={
            <RequestForQuotationRef
              serviceType={serviceType}
              referenceFiles={requestState.uploadedReferenceFiles ?? []}
              handleUpdateRfqInfo={handleUpdateRfqInfo}
              onOpenDialog={(type: keyof DialogTypes) =>
                setDialogState({ ...dialogState, [type]: true })
              }
            />
          }
          // 프로젝트 정보
          projectInfoComponent={
            <RequestForQuotationProjectInfo
              matches={matches}
              serviceType={serviceType}
              maxEstimatedBudget={requestState.maxEstimatedBudget}
              handleUpdateRfqInfo={handleUpdateRfqInfo}
            />
          }
          //상담 시작 방식
          consultationMethodComponent={
            <RequestForQuotationConsultMethod
              handleUpdateRfqInfo={handleUpdateRfqInfo}
            />
          }
          // 제출
          submitComponent={
            <RequestForQuotationSubmit
              onCheck={onCheck}
              serviceType={serviceType}
            />
          }
        />
      </FormProvider>
    </CommonLayout>
  );
};

export default RequestForQuotationContainer;
